import React, { useEffect, useState, useCallback } from "react";
import { Form, Formik } from "formik";
import { Button, Paper, Box, TextField, Grid } from "@mui/material";
import { styled } from '@mui/material/styles';
import ClientModel, { ClientData, clientSchema } from "../../core/api/clients/ClientModel";
import { useNavigate } from "react-router-dom";
import { useNotifications } from "../../hooks/NotificationProvider";
import { CompanyDetailsSection } from "./components/CompanyDetailsSection";
import { ContactDetailsSection } from "./components/ContactDetailsSection";
import { ClientDetailsSection } from "./components/ClientDetailsSection";
import { translations } from "../../translations/common";

interface ClientFormProps {
  clientId?: string | null;
}

// Form container styling
const FormContainer = styled('div')(() => ({
  width: "100%",
}));


// Last grid item with right margin
const LastGridItem = styled(Grid)(({ theme }) => ({
  paddingRight: theme.spacing(2),
}));

// Comment field styling
const CommentField = styled(TextField)(({ theme }) => ({
  marginLeft: theme.spacing(1),
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
  overflowY: "scroll",
  '& .MuiInputLabel-root': {
    marginTop: '6px'
  }
}));

// Submit button styling
const SubmitButton = styled(Button)(({ theme }) => ({
  marginLeft: theme.spacing(1),
  marginRight: theme.spacing(1),
  marginBottom: theme.spacing(1),
}));

const defaultClient: ClientData = {
  id: "",
  companyTitle: "",
  companyCode: "",
  companyVatCode: "",
  companyAddress: "",
  addressee: "",
  companyPhone: "",
  emails: [],
  clientState: "",
  validFrom: "",
  validTo: "",
  responsibleUser: "",
  responsibleUserName: "",
  comment: "",
};

export const ClientForm: React.FC<ClientFormProps> = ({ clientId }) => {
  const [client, setClient] = useState<ClientData>(defaultClient);
  const notifications = useNotifications();
  const navigate = useNavigate();

  const fetchClient = useCallback(async () => {
    if (clientId) {
      try {
        const fetchedClient = await ClientModel.findOne(clientId);
        setClient(fetchedClient);
      } catch (error) {
        console.error('Failed to fetch client:', error);
        notifications.notify({
          level: "error",
          message: translations.clientForm.errors.loadClient,
        });
      }
    }
  }, [clientId, notifications]);

  useEffect(() => {
    fetchClient();
  }, [fetchClient]);

  const handleSubmit = useCallback(async (values: ClientData, actions: any) => {
    try {
      const entityId = await ClientModel.save(values);
      if (!clientId && entityId?.id) {
        navigate(`/client/${entityId.id}`);
      }
      notifications.notify({
        level: "success",
        message: translations.clientForm.success.saved,
      });
    } catch (error) {
      const data = (error as { response?: { data: any } }).response?.data;
      if (data?.description) {
        notifications.notify({
          level: "error",
          message: data.description,
        });
      } else if (data?.errors) {
        data.errors.forEach((element: any) => {
          actions.setFieldError(element.field, element.defaultMessage);
        });
      } else {
        notifications.notify({
          level: "error",
          message: translations.clientForm.errors.server,
        });
      }
    } finally {
      actions.setSubmitting(false);
    }
  }, [navigate, notifications, clientId]);

  return (
    <Paper>
      <Formik
        initialValues={client}
        enableReinitialize={true}
        onSubmit={handleSubmit}
        validationSchema={clientSchema}
      >
        {({ values, touched, errors, handleBlur, handleChange, isSubmitting }) => (
          <Form>
            <FormContainer>
              <Grid container spacing={3}>
                <Grid size={4}>
                  <CompanyDetailsSection
                    values={values}
                    errors={errors}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    className="margin-container"
                  />
                </Grid>

                <Grid size={4}>
                  <ContactDetailsSection
                    values={values}
                    errors={errors}
                    touched={touched}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    className="margin-container"
                    emailInputClassName="email-input"
                    emailsContainerClassName="emails-container"
                  />
                </Grid>

                <LastGridItem size={4}>
                  <ClientDetailsSection
                    values={values}
                    errors={errors}
                    touched={touched}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    className="margin-container"
                    textFieldClassName="text-field"
                  />
                </LastGridItem>
              </Grid>

              <Box sx={{ mt: 2, mb: 2, pr: 2, pl: 1 }}>
                <TextField
                  id="comment"
                  name="comment"
                  label="Komentaras"
                  onChange={handleChange}
                  value={values.comment}
                  onBlur={handleBlur}
                  multiline
                  minRows={4}
                  maxRows={12}
                  fullWidth
                  variant="outlined"
                />
              </Box>

              <Grid container>
                <Grid size="grow" display="flex" justifyContent="flex-end">
                  <SubmitButton
                    type="submit"
                    disabled={isSubmitting}
                    variant="contained"
                    color="primary"
                  >
                    {translations.clientForm.save}
                  </SubmitButton>
                </Grid>
              </Grid>
            </FormContainer>
          </Form>
        )}
      </Formik>
    </Paper>
  );
};
